import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, Zap } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useEffect } from "react";
import toast from "react-hot-toast";

export default function LoginPage() {
  const { login, profile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [justLoggedIn, setJustLoggedIn] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success("Welcome back!");
      setJustLoggedIn(true);
    } catch (err) {
      setLoading(false);
      toast.error(err.message.includes("user-not-found") ? "Account not found" : err.message.includes("wrong-password") ? "Incorrect password" : "Login failed. Try again.");
    }
  }

  // Redirect after login based on role
  useEffect(() => {
    if (justLoggedIn && profile) {
      if (profile.role === "admin") {
        navigate("/admin", { replace: true });
      } else {
        navigate(from || "/shop", { replace: true });
      }
    }
  }, [justLoggedIn, profile, navigate, from]);

  return (
    <div className="login-split-container">
      {/* Left panel (desktop) */}
      <div className="login-left-panel">
        <div style={{ position: "absolute", top: -100, right: -100, width: 300, height: 300, background: "radial-gradient(circle, rgba(232,71,26,0.2) 0%, transparent 70%)", borderRadius: "50%", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -80, left: -60, width: 250, height: 250, background: "radial-gradient(circle, rgba(245,166,35,0.12) 0%, transparent 70%)", borderRadius: "50%", pointerEvents: "none" }} />
        <Link to="/" style={{ display: "inline-flex", alignItems: "center", marginBottom: 40, position: "relative", textDecoration: "none" }}>
          <img src="/images/Afriportstore.png" alt="Afriport" style={{ height: 52, width: "auto", objectFit: "contain" }} />
        </Link>
        <h1 style={{ fontFamily: "var(--font-head)", fontSize: 38, fontWeight: 800, lineHeight: 1.15, marginBottom: 18, color: "#FFFFFF" }}>
          Everything for your home,<br />
          <span style={{ color: "var(--brand)" }}>office & energy needs.</span>
        </h1>
        <p style={{ color: "#9CA3AF", fontSize: 15, lineHeight: 1.75 }}>
          Your one-stop marketplace for solar energy & inverters, computers & connectivity, household appliances, and quality furniture — delivered reliably across Nigeria.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 40 }}>
          {["Door delivery & pickup options nationwide", "100% genuine products with warranty", "Paystack-secured payments", "Business sellers earn 90%"].map(t => (
            <div key={t} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: "#D1D5DB" }}>
              <div style={{ width: 20, height: 20, background: "var(--brand)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Zap size={11} color="#fff" />
              </div>
              {t}
            </div>
          ))}
        </div>
      </div>

      {/* Right panel */}
      <div className="login-right-panel">
        <div style={{ width: "100%", maxWidth: 420 }}>
          <div className="login-mobile-logo">
            <Link to="/" style={{ display: "inline-flex", alignItems: "center" }}>
              <img src="/images/Afriportstore.png" alt="Afriport" style={{ height: 48, width: "auto", objectFit: "contain" }} />
            </Link>
          </div>
          <h2 style={{ fontFamily: "var(--font-head)", fontSize: 28, fontWeight: 700, marginBottom: 8 }}>Sign in</h2>
          <p style={{ color: "var(--text-muted)", marginBottom: 32, fontSize: 15 }}>
            Don't have an account? <Link to="/register" style={{ color: "var(--brand)", fontWeight: 500 }}>Create one</Link>
          </p>

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div className="form-group">
              <label>Email address</label>
              <input className="form-input" type="email" placeholder="you@example.com" required
                value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            </div>

            <div className="form-group">
              <label>Password</label>
              <div style={{ position: "relative" }}>
                <input className="form-input" type={showPwd ? "text" : "password"} placeholder="Your password" required
                  style={{ paddingRight: 44 }}
                  value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
                <button type="button" onClick={() => setShowPwd(!showPwd)}
                  style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", border: "none", background: "transparent", color: "var(--text-muted)", cursor: "pointer" }}>
                  {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Link to="/forgot-password" style={{ fontSize: 13, color: "var(--brand)" }}>Forgot password?</Link>
            </div>

            <button className="btn btn-primary btn-lg w-full" type="submit" disabled={loading}>
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
